import React, { useState, useContext, useEffect, useRef } from "react";
import styled from "styled-components";
import { motion, AnimatePresence } from "framer-motion";
import { DirectionContext } from "../App";
import API_ENDPOINTS from "../config/api";
import Cookies from "js-cookie";
import {
  patchTermsAgreement,
  getInitialTermsFromLocal,
} from "../config/termsAgreement";

// 가입일은 2025년 1월 28일 이전 가입자만 본 소송 대상
const MAX_JOIN_DATE = "2025-01-28";
// 백엔드로 보낼 placeholder 이미지 (사용자에게 실제 이미지를 받지 않음)
const PLACEHOLDER_IMAGE_PATH = "/png/mainImage.png";

const ScreenshotSubmission = () => {
  const { navigateTo } = useContext(DirectionContext);

  // 폼 상태
  // joinDate 초기값은 빈 문자열 — 사용자가 반드시 직접 선택해야 폼 유효
  const [joinDate, setJoinDate] = useState("");
  const [membershipStatus, setMembershipStatus] = useState(""); // "ACTIVE" | "INACTIVE"
  const [membershipType, setMembershipType] = useState(""); // "PAID" | "FREE"
  const [membershipFee, setMembershipFee] = useState("");

  const fileInputRef = useRef(null);
  const dateInputRef = useRef(null);
  const [uploadedFile, setUploadedFile] = useState(null); // { file, preview }
  const [showZoom, setShowZoom] = useState(false);

  // 제출 관련 상태
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState("");
  const [applicationNumber, setApplicationNumber] = useState(null);
  const [applicationId, setApplicationId] = useState(null);
  const [isCreatingApplication, setIsCreatingApplication] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState("");

  // 페이지 로드 시 스크롤 초기화 + 쿠키 복원
  useEffect(() => {
    window.scrollTo(0, 0);
    document.body.scrollTop = 0;
    document.documentElement.scrollTop = 0;

    const cookieApplicationNumber = Cookies.get("application_number");
    const cookieApplicationId = Cookies.get("application_id");
    if (cookieApplicationNumber) setApplicationNumber(cookieApplicationNumber);
    if (cookieApplicationId) setApplicationId(cookieApplicationId);
  }, []);

  const pageVariants = {
    initial: { opacity: 0 },
    in: { opacity: 1, transition: { duration: 0.5 } },
    out: { opacity: 0, transition: { duration: 0.3 } },
  };

  const contentVariants = {
    initial: { opacity: 0, y: 20 },
    in: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, delay: 0.2, staggerChildren: 0.1 },
    },
  };

  // 검증 헬퍼
  const isJoinDateValid = joinDate !== "" && joinDate <= MAX_JOIN_DATE;
  const isJoinDateInvalid = joinDate !== "" && joinDate > MAX_JOIN_DATE;
  const isFeeValid = membershipType !== "PAID" || membershipFee.trim() !== "";
  const isFormValid =
    isJoinDateValid &&
    membershipStatus !== "" &&
    membershipType !== "" &&
    isFeeValid &&
    !!uploadedFile;

  // 가입비 입력 (숫자만)
  const handleFeeChange = (e) => {
    const numeric = e.target.value.replace(/[^0-9]/g, "");
    setMembershipFee(numeric);
  };

  // 파일 업로드 핸들러
  const handleUploadAreaClick = () => {
    if (uploadedFile) return; // 이미 업로드된 경우 미리보기 클릭은 무시
    fileInputRef.current?.click();
  };

  const handleOpenFilePicker = () => {
    fileInputRef.current?.click();
  };

  const handleOpenDuoLink = () => {
    window.open(
      "https://m.duo.co.kr/personalinfo/",
      "_blank",
      "noopener,noreferrer",
    );
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (uploadedFile?.preview) URL.revokeObjectURL(uploadedFile.preview);
    setUploadedFile({
      file,
      preview: URL.createObjectURL(file),
    });
  };

  const handleRemoveFile = (e) => {
    e.stopPropagation();
    if (uploadedFile?.preview) URL.revokeObjectURL(uploadedFile.preview);
    setUploadedFile(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  // 컴포넌트 언마운트 시 ObjectURL 메모리 정리
  useEffect(() => {
    return () => {
      if (uploadedFile?.preview) URL.revokeObjectURL(uploadedFile.preview);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 신청서 생성
  const createApplication = async () => {
    if (applicationNumber && applicationId) {
      return { applicationNumber, applicationId };
    }

    setIsCreatingApplication(true);
    setUploadError("");

    try {
      const token = Cookies.get(API_ENDPOINTS.COOKIE_CONFIG.KEYS.ACCESS_TOKEN);
      if (!token) {
        throw new Error("인증 토큰이 없습니다. 다시 로그인해주세요.");
      }

      const userInfoResponse = await fetch(
        `${API_ENDPOINTS.BASE_URL}/clients/me`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (userInfoResponse.ok) {
        const userInfoData = await userInfoResponse.json();

        const hasCompletedApplication =
          userInfoData.applications &&
          userInfoData.applications.some(
            (app) => app.lawsuitId === 3 && app.status === "PAYMENT_COMPLETED",
          );

        const normalizedPhone =
          userInfoData.phoneNumber?.replace(/[\s-]/g, "") || "";

        if (hasCompletedApplication && normalizedPhone !== "01066076676") {
          setIsCreatingApplication(false);
          setModalType("already-completed");
          setShowModal(true);
          return;
        }
      }

      // 듀오 가입 정보 (입력 폼 → 백엔드 필드명 매핑)
      const isPaidUser = membershipType === "PAID";
      const joinFee = isPaidUser ? Number(membershipFee || 0) : 0;
      const isWithdrawn = membershipStatus === "INACTIVE";

      const baseRequestBody = {
        lawsuitId: 3,
        isUsimReplaced: false,
        damageDescription: "개인정보 유출로 인한 보이스피싱 피해를 입었습니다.",
        bankCode: Cookies.get("bankCode"),
        accountNumber: Cookies.get("accountNumber"),
        accountHolder: Cookies.get("accountHolder"),
        joinDate: joinDate || null,
        isPaidUser,
        joinFee,
        isWithdrawn,
      };

      const applicationType = Cookies.get("applicationType") || "self";
      let response;

      if (applicationType === "representative") {
        const coOwnersJson = Cookies.get("coOwners");
        let coOwners = [];
        if (coOwnersJson) {
          try {
            coOwners = JSON.parse(coOwnersJson);
          } catch (e) {}
        }

        const requestBody = {
          ...baseRequestBody,
          baseFee: 10000,
          contingencyFeePercentage: 10,
          applicationType: "REPRESENTATIVE",
          // 백엔드 DTO 키 오타에 맞춤 (coSigners → coSigers)
          coSigers: coOwners.map((owner) => ({
            name: owner.name,
            phone: owner.phone,
          })),
        };

        response = await fetch(`${API_ENDPOINTS.BASE_URL}/applications`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(requestBody),
        });
      } else {
        const requestBody = {
          ...baseRequestBody,
          baseFee: 10000,
          contingencyFeePercentage: 10,
          applicationType: "INDIVIDUAL",
        };

        response = await fetch(`${API_ENDPOINTS.BASE_URL}/applications`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(requestBody),
        });
      }

      if (!response.ok) {
        const errorData = await response.json();
        if (response.status === 409) {
          throw new Error(
            errorData.message ||
              "공동신청자 중 이미 결제 완료한 신청서가 있는 사용자가 있습니다. 공동신청자 목록을 확인해주세요.",
          );
        }
        throw new Error(errorData.message || "제출에 실패했습니다.");
      }

      const responseData = await response.json();
      // 새 응답 형태: { success, message, data: { id, applicationNumber } }
      const created = responseData.data || responseData;

      const appNumber =
        created.applicationNumber ||
        responseData.applicationNumber ||
        responseData.application?.applicationNumber;
      if (!appNumber) {
        throw new Error(
          "신청번호를 받지 못했습니다. 나중에 다시 시도해주세요.",
        );
      }

      const appId =
        created.id ||
        created.applicationId ||
        responseData.applicationId ||
        responseData.id ||
        responseData.application?.applicationId ||
        responseData.application?.id;
      if (!appId) {
        throw new Error(
          "신청서 ID를 받지 못했습니다. 나중에 다시 시도해주세요.",
        );
      }

      Cookies.set("application_number", appNumber, { expires: 7, path: "/" });
      Cookies.set("application_id", appId, { expires: 7, path: "/" });
      setApplicationId(appId);

      // 본인인증 단계에서 로컬스토리지에 임시 저장해 둔 약관 동의값을
      // 신청서 생성 직후 서버에 반영 (개인정보 수집 / 이용약관 / 마케팅 수신)
      try {
        const initialTerms = getInitialTermsFromLocal();
        if (initialTerms) {
          await patchTermsAgreement(appId, initialTerms);
        }
      } catch (termsError) {
        // 약관 동의 저장 실패는 신청서 생성 흐름을 막지 않는다 (로그만 남김)
        console.error("초기 약관 동의 저장 실패:", termsError);
      }

      const baseFee =
        responseData.baseFee || responseData.application?.baseFee || 10000;
      const contingencyFeePercentage =
        responseData.contingencyFeePercentage ||
        responseData.application?.contingencyFeePercentage ||
        10;

      Cookies.set("lawsuit_amount", baseFee, { expires: 7, path: "/" });
      Cookies.set("lawsuit_fee", baseFee, { expires: 7, path: "/" });
      Cookies.set("lawsuit_success_fee_percentage", contingencyFeePercentage, {
        expires: 7,
        path: "/",
      });

      const coSigners = responseData.coSigners || responseData.coApplicants;
      if (coSigners && coSigners.length > 0) {
        Cookies.set(
          "coApplicantIds",
          JSON.stringify(coSigners.map((cs) => cs.id)),
          { expires: 7, path: "/" },
        );
      }

      setApplicationNumber(appNumber);
      setApplicationId(appId);
      return { applicationNumber: appNumber, applicationId: appId };
    } catch (error) {
      console.error("신청서 생성 오류:", error);
      setUploadError(
        error.message || "신청서 생성에 실패했습니다. 다시 시도해주세요.",
      );
      throw error;
    } finally {
      setIsCreatingApplication(false);
    }
  };

  // 완료하기: 폼 저장 → placeholder 이미지 업로드 → 다음 페이지
  const handleComplete = async () => {
    if (!isFormValid || isUploading || isCreatingApplication) return;

    setIsUploading(true);
    setUploadError("");

    try {
      let currentApplicationId = applicationId;
      if (!applicationNumber || !currentApplicationId) {
        const result = await createApplication();
        if (result && result.applicationId) {
          currentApplicationId = result.applicationId;
          setApplicationId(result.applicationId);
        } else {
          throw new Error("신청서 생성에 실패했습니다. 다시 시도해주세요.");
        }
      }

      const token = Cookies.get(API_ENDPOINTS.COOKIE_CONFIG.KEYS.ACCESS_TOKEN);
      if (!token) {
        throw new Error("인증 토큰이 없습니다. 다시 로그인해주세요.");
      }
      if (!currentApplicationId) {
        throw new Error("신청서 ID가 없습니다. 다시 시도해주세요.");
      }

      // 폼 데이터 쿠키 저장
      Cookies.set("membership_join_date", joinDate, {
        expires: 7,
        path: "/",
      });
      Cookies.set("membership_status", membershipStatus, {
        expires: 7,
        path: "/",
      });
      Cookies.set("membership_type", membershipType, {
        expires: 7,
        path: "/",
      });
      if (membershipType === "PAID") {
        Cookies.set("membership_fee", membershipFee, {
          expires: 7,
          path: "/",
        });
      } else {
        Cookies.remove("membership_fee", { path: "/" });
      }

      // 사용자가 업로드한 파일이 있으면 사용, 없으면 placeholder로 fallback
      let fileToUpload;
      if (uploadedFile?.file) {
        fileToUpload = uploadedFile.file;
      } else {
        const imgRes = await fetch(PLACEHOLDER_IMAGE_PATH);
        if (!imgRes.ok) {
          throw new Error("기본 이미지를 불러올 수 없습니다.");
        }
        const imgBlob = await imgRes.blob();
        fileToUpload = new File([imgBlob], "placeholder.png", {
          type: imgBlob.type || "image/png",
        });
      }

      const formData = new FormData();
      formData.append("file", fileToUpload);

      const uploadUrl = `${API_ENDPOINTS.BASE_URL}/uploads/lawsuit/duo/membership-proof?applicationId=${currentApplicationId}`;
      const response = await fetch(uploadUrl, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });

      if (response.status !== 201) {
        const errorData = await response.json();
        throw new Error(errorData.message || "제출에 실패했습니다.");
      }

      const responseData = await response.json();
      if (responseData.fileKey) {
        Cookies.set("screenshot_fileKey", responseData.fileKey, {
          expires: API_ENDPOINTS.COOKIE_CONFIG.EXPIRES.ACCESS_TOKEN,
          ...API_ENDPOINTS.COOKIE_CONFIG.OPTIONS,
        });
      }

      navigateTo("/news-proof");
    } catch (error) {
      setUploadError(error.message || "제출 중 오류가 발생했습니다.");
    } finally {
      setIsUploading(false);
    }
  };

  const handleModalConfirm = () => {
    setShowModal(false);
    if (modalType === "already-completed") {
      Cookies.remove(API_ENDPOINTS.COOKIE_CONFIG.KEYS.ACCESS_TOKEN);
      navigateTo("/select");
    }
  };

  return (
    <PageBackground>
      <Container
        variants={pageVariants}
        initial="initial"
        animate="in"
        exit="out"
      >
        <Content variants={contentVariants} initial="initial" animate="in">
          <HeroSection>
            <HeroTitle>
              듀오 개인정보 유출 확인
              <br />
              <Strong>'전체 화면'</Strong>을 제출해 주세요.
            </HeroTitle>
            <HeroDescription>
              듀오에서 공식적으로 조회 페이지를 제공합니다.
            </HeroDescription>
          </HeroSection>

          <CaptureButton
            onClick={handleOpenDuoLink}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            유출 확인 페이지 이동하기
          </CaptureButton>

          <ExampleCard>
            <ExampleTitle>개인정보 유출 내역 캡처 예시</ExampleTitle>
            <ExampleSubtitle>
              클릭 시 큰 화면으로 확인할 수 있습니다
            </ExampleSubtitle>
            <ExampleThumb onClick={() => setShowZoom(true)}>
              <ExampleImage
                src="/png/eximg.png"
                alt="개인정보 유출 내역 캡처 예시"
              />
            </ExampleThumb>
          </ExampleCard>

          <UploadCard>
            <UploadCardTitle>유출 확인 화면 제출하기</UploadCardTitle>
            <UploadCardSubtitle>
              유출 확인 결과가 보이도록 캡처하여 제출해주세요
              <br />
              확인 내역이 없다면 먼저 유출 여부를 조회해주세요.
            </UploadCardSubtitle>

            <UploadArea
              onClick={handleUploadAreaClick}
              $hasFile={!!uploadedFile}
            >
              {uploadedFile ? (
                <UploadedPreview>
                  <PreviewImage
                    src={uploadedFile.preview}
                    alt="업로드된 이미지"
                  />
                  <RemoveButton onClick={handleRemoveFile}>
                    <RemoveIcon src="/png/delete.png" alt="삭제" />
                  </RemoveButton>
                </UploadedPreview>
              ) : (
                <UploadPlaceholder>
                  <UploadHint>이곳에 파일을 업로드해주세요</UploadHint>
                </UploadPlaceholder>
              )}
            </UploadArea>
            <HiddenFileInput
              type="file"
              ref={fileInputRef}
              accept="image/*"
              onChange={handleFileSelect}
            />

            <HintRow>
              <HintIcon src="/png/iicon2.png" alt="" />
              <HintText>유출 항목이 모두 보이게 캡쳐해 주세요</HintText>
            </HintRow>

            <UploadCardButton
              onClick={handleOpenFilePicker}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              파일 업로드
            </UploadCardButton>
          </UploadCard>

          {/* 가입일 */}
          <FormSection>
            <SectionTitle>듀오 가입일</SectionTitle>
            <FormHint>
              가입일을 선택해 주세요. 정확한 일자가 기억나지 않으시면{" "}
              <strong>대략적인 가입일</strong>로 선택하셔도 됩니다.
            </FormHint>
            <DateInputWrapper
              onClick={() => {
                // PC 브라우저(Chrome/Edge/Firefox)에서 picker 강제 오픈
                // 모바일은 native label 동작으로 이미 처리됨
                const input = dateInputRef.current;
                if (input?.showPicker) {
                  try {
                    input.showPicker();
                  } catch (err) {
                    // showPicker 실패 시 네이티브 폴백 (이미 label이 처리)
                  }
                }
              }}
            >
              <MonthInput
                ref={dateInputRef}
                type="date"
                value={joinDate}
                max={MAX_JOIN_DATE}
                onChange={(e) => setJoinDate(e.target.value)}
                $hasError={isJoinDateInvalid}
              />
              {!joinDate && (
                <DatePlaceholder>가입일을 선택해 주세요</DatePlaceholder>
              )}
            </DateInputWrapper>
            {isJoinDateInvalid ? (
              <FormError>
                ※ 2025년 1월 28일까지의 가입자만 본 소송 대상입니다.
              </FormError>
            ) : (
              <FormSubHint>
                ※ 2025년 1월 28일까지의 가입자만 본 소송 대상입니다.
              </FormSubHint>
            )}
          </FormSection>

          {/* 회원 상태 */}
          <FormSection>
            <SectionTitle>회원 상태</SectionTitle>
            <ToggleGroup>
              <ToggleButton
                type="button"
                $active={membershipStatus === "ACTIVE"}
                onClick={() => setMembershipStatus("ACTIVE")}
              >
                현재 회원
              </ToggleButton>
              <ToggleButton
                type="button"
                $active={membershipStatus === "INACTIVE"}
                onClick={() => setMembershipStatus("INACTIVE")}
              >
                휴면 / 탈퇴 처리
              </ToggleButton>
            </ToggleGroup>
          </FormSection>

          {/* 회원 유형 */}
          <FormSection>
            <SectionTitle>회원 유형</SectionTitle>
            <ToggleGroup>
              <ToggleButton
                type="button"
                $active={membershipType === "PAID"}
                onClick={() => setMembershipType("PAID")}
              >
                유료 회원
              </ToggleButton>
              <ToggleButton
                type="button"
                $active={membershipType === "FREE"}
                onClick={() => setMembershipType("FREE")}
              >
                무료 회원
              </ToggleButton>
            </ToggleGroup>

            {membershipType === "PAID" && (
              <FeeInputContainer>
                <FormHint>가입 시 결제하신 가입비를 입력해 주세요.</FormHint>
                <FeeInputWrapper>
                  <FeeInput
                    type="text"
                    inputMode="numeric"
                    value={
                      membershipFee
                        ? Number(membershipFee).toLocaleString()
                        : ""
                    }
                    onChange={handleFeeChange}
                    placeholder="예: 500,000"
                  />
                  <FeeUnit>원</FeeUnit>
                </FeeInputWrapper>
                <FormSubHint>
                  가입비가 정확히 기억나지 않으시면{" "}
                  <strong>대략적인 금액</strong>을 입력하셔도 됩니다. 추후
                  가입비 증거 보완을 요청드릴 수 있습니다.
                </FormSubHint>
              </FeeInputContainer>
            )}
          </FormSection>

          {uploadError && <ErrorAlert>{uploadError}</ErrorAlert>}

          <CompleteButton
            onClick={handleComplete}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            disabled={!isFormValid || isUploading || isCreatingApplication}
          >
            {isCreatingApplication
              ? "신청서 생성 중..."
              : isUploading
                ? "제출 중..."
                : "다음"}
          </CompleteButton>
        </Content>
      </Container>

      <AnimatePresence>
        {showZoom && (
          <ZoomOverlay
            onClick={() => setShowZoom(false)}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <ZoomScroll onClick={(e) => e.stopPropagation()}>
              <ZoomCard>
                <ZoomHeader>
                  <ZoomTitle>개인정보 유출 내역 캡처 예시</ZoomTitle>
                  <ZoomClose onClick={() => setShowZoom(false)}>×</ZoomClose>
                </ZoomHeader>
                <ZoomImageWrapper>
                  <ZoomImage src="/png/eximg.png" alt="유출 캡처 예시" />
                </ZoomImageWrapper>
              </ZoomCard>
            </ZoomScroll>
          </ZoomOverlay>
        )}
      </AnimatePresence>

      {showModal && (
        <ModalOverlay>
          <ModalContainer>
            <ModalContent>
              {modalType === "already-completed" ? (
                <ModalText>
                  이미 집단소송 신청을 완료하셨습니다.
                  <br />
                  소송 진행 상황은 메인 페이지에서 확인하실 수 있습니다.
                </ModalText>
              ) : null}
              <ModalButton onClick={handleModalConfirm}>확인</ModalButton>
            </ModalContent>
          </ModalContainer>
        </ModalOverlay>
      )}
    </PageBackground>
  );
};

// 스타일 컴포넌트
const HeroSection = styled.div`
  width: 100%;
`;

const HeroTitle = styled.h1`
  font-size: 22px;
  font-weight: 500;
  line-height: 1.35;
  color: #333639;
  margin: 0 0 10px 0;
  word-break: keep-all;
`;

const HeroDescription = styled.p`
  font-size: 16px;
  font-weight: 400;
  line-height: 1.5;
  color: #595d63;
  margin: 0;
  word-break: keep-all;
`;

const Strong = styled.span`
  font-weight: 700;
  color: #333639;
`;

const CaptureButton = styled(motion.button)`
  display: flex;
  width: 100%;
  height: 56px;
  padding: 0 14px;
  justify-content: center;
  align-items: center;
  border-radius: 8px;
  background: #ffffff;
  color: #e31c20;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  border: 1px solid #ffe8e8;
  margin-top: 16px;
  margin-bottom: 16px;
`;

const HintRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  margin-top: 12px;
  margin-bottom: 12px;
`;

const HintIcon = styled.img`
  flex-shrink: 0;
  width: 16px;
  height: 16px;
  display: block;
`;

const HintText = styled.span`
  font-size: 12px;
  font-weight: 500;
  color: #9091a0;
`;

const ExampleCard = styled.div`
  width: 100%;
  background-color: white;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 16px;
  box-sizing: border-box;
`;

const ExampleTitle = styled.h3`
  font-size: 15px;
  font-weight: 500;
  color: #4b5563;
  margin: 0 0 4px 0;
`;

const ExampleSubtitle = styled.p`
  font-size: 14px;
  font-weight: 500;
  color: #9091a0;
  margin: 0 0 12px 0;
  line-height: 1.5;
`;

const ExampleThumb = styled.button`
  width: 96px;
  height: 96px;
  position: relative;
  border: 1px solid #e5e5e5;
  border-radius: 8px;
  background-color: white;
  padding: 0;
  overflow: hidden;
  cursor: zoom-in;
  display: block;
  text-align: left;
`;

const ExampleImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

const UploadCard = styled.div`
  width: 100%;
  background-color: white;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 56px;
  box-sizing: border-box;
`;

const UploadCardTitle = styled.h3`
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 700;
  line-height: 140%;
  color: #4b5563;
  font-feature-settings:
    "liga" off,
    "clig" off;
  margin: 0 0 12px 0;
`;

const UploadCardSubtitle = styled.p`
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 125%;
  letter-spacing: -0.14px;
  color: #9091a0;
  margin: 0 0 14px 0;
`;

const UploadCardButton = styled(motion.button)`
  display: flex;
  width: 100%;
  height: 44px;
  padding: 0 14px;
  margin-top: 12px;
  justify-content: center;
  align-items: center;
  border-radius: 10px;
  background: #e31c20;
  color: #ffffff;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  border: none;
`;

const ZoomOverlay = styled(motion.div)`
  position: fixed;
  inset: 0;
  background-color: rgba(60, 60, 60, 0.75);
  z-index: 9999;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 40px 24px;
`;

const ZoomScroll = styled.div`
  width: 100%;
  max-width: 420px;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 16px;
`;

const ZoomCard = styled.div`
  width: 100%;
  background-color: #ffffff;
  border-radius: 20px;
  padding: 24px 20px 32px 20px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 24px;
`;

const ZoomHeader = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

const ZoomTitle = styled.h3`
  flex: 1;
  text-align: left;
  font-size: 16px;
  font-weight: 600;
  color: #4b5563;
  margin: 0;
`;

const ZoomImageWrapper = styled.div`
  width: 100%;
  border: 1px solid #e8ecf4;
  border-radius: 30px;
  padding: 16px;
  box-sizing: border-box;
  background-color: #ffffff;
`;

const ZoomImage = styled.img`
  width: 100%;
  height: auto;
  border-radius: 8px;
  display: block;
`;

const ZoomClose = styled.button`
  flex-shrink: 0;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background-color: transparent;
  color: #4b5563;
  font-size: 22px;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
  padding: 0;
`;

const PageBackground = styled.div`
  width: 100%;
  min-height: 100vh;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding: 0;
  background-color: #f7f7f7;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;

  @media (min-width: 769px) {
    padding: 40px 20px 60px;
    background-color: #f7f7f7;
  }
`;

const Container = styled(motion.div)`
  position: relative;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  background-color: #f7f7f7;

  @media (max-width: 768px) {
    min-height: auto;
    box-shadow: none;
    padding: 0 0 80px 0;
    max-width: 100%;
    margin: 0;
    border-radius: 0;
  }

  @media (min-width: 769px) {
    border-radius: 20px;
    min-height: auto;
    max-height: none;
    max-width: 470px;
    margin: 0 auto;
    box-shadow: 0 0 20px rgba(0, 0, 0, 0.2);
    padding: 20px;
  }
`;

const Content = styled(motion.div)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  text-align: left;
  width: 100%;
  position: relative;
  padding: 20px;

  @media (max-width: 768px) {
    padding: 60px 20px 80px;
  }

  @media (min-width: 769px) {
    padding: 40px 30px 40px 30px;
  }
`;

const Header = styled.div`
  width: 100%;
  margin-bottom: 24px;
  color: #333;
  z-index: 2;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const SubTitle = styled.h2`
  font-size: 28px;
  font-weight: 700;
  line-height: 1.3;
  margin-bottom: 10px;
  word-break: keep-all;
  color: #333;
  text-align: left;
  width: 100%;
`;

const Description = styled.p`
  font-size: 16px;
  color: #666;
  margin-bottom: 0px;
  line-height: 1.4;
`;

const InfoSection = styled.div`
  width: 100%;
  background-color: white;
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 16px;
`;

const FormSection = styled.div`
  width: 100%;
  background-color: white;
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 16px;
`;

const SectionTitle = styled.h3`
  font-size: 18px;
  font-weight: 700;
  color: #333;
  margin: 0 0 12px 0;
`;

const InfoText = styled.p`
  font-size: 14px;
  color: #555;
  line-height: 1.55;
  margin: 0 0 10px 0;

  strong {
    color: #1d3557;
    font-weight: 600;
  }
`;

const InfoNote = styled.p`
  font-size: 13px;
  color: #777;
  line-height: 1.55;
  margin: 14px 0 0 0;
  background-color: #f7f9fc;
  padding: 12px 14px;
  border-radius: 8px;
`;

const ModeToggleLabel = styled.p`
  font-size: 14px;
  color: #555;
  font-weight: 600;
  margin: 14px 0 8px 0;
`;

const UploadArea = styled.div`
  margin-top: 4px;
  width: 100%;
  min-height: 120px;
  border: 2px dashed ${(props) => (props.$hasFile ? "#E31C20" : "#E8ECF4")};
  border-radius: 12px;
  background-color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: ${(props) => (props.$hasFile ? "default" : "pointer")};
  transition:
    border-color 0.15s ease,
    background-color 0.15s ease;
  overflow: hidden;
  padding: 12px;

  &:hover {
    border-color: ${(props) => (props.$hasFile ? "#E31C20" : "#d4dae6")};
  }
`;

const UploadPlaceholder = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 8px;
`;

const UploadIconImg = styled.img`
  width: 40px;
  height: 40px;
  object-fit: contain;
  opacity: 0.5;
  filter: grayscale(1);
`;

const UploadHint = styled.div`
  font-size: 14px;
  color: #9091a0;
  text-align: center;
  line-height: 1.45;
  font-weight: 400;

  small {
    display: inline-block;
    margin-top: 4px;
    font-size: 12px;
    color: #9aa3b3;
    font-weight: 400;
  }
`;

const UploadedPreview = styled.div`
  position: relative;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const PreviewImage = styled.img`
  max-width: 100%;
  max-height: 280px;
  object-fit: contain;
  border-radius: 8px;
`;

const RemoveButton = styled.button`
  position: absolute;
  top: 6px;
  right: 6px;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background-color: rgba(0, 0, 0, 0.45);
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
  z-index: 2;

  &:hover {
    background-color: rgba(0, 0, 0, 0.6);
  }
`;

const RemoveIcon = styled.img`
  width: 16px;
  height: 16px;
  object-fit: contain;
  filter: brightness(0) invert(1);
`;

const HiddenFileInput = styled.input`
  display: none;
`;

const FormHint = styled.p`
  font-size: 14px;
  color: #555;
  line-height: 1.5;
  margin: 0 0 12px 0;

  strong {
    color: #1d3557;
    font-weight: 600;
  }
`;

const FormSubHint = styled.p`
  font-size: 12px;
  color: #888;
  line-height: 1.5;
  margin: 8px 0 0 0;
`;

const FormError = styled.p`
  font-size: 13px;
  color: #ff4c4c;
  font-weight: 600;
  line-height: 1.5;
  margin: 8px 0 0 0;
`;

// label로 감싸면 native 브라우저가 클릭/탭 시 input의 click+focus를 자동 처리
// → iOS Safari·Android Chrome에서도 placeholder 영역 탭 시 picker 정상 오픈
const DateInputWrapper = styled.label`
  position: relative;
  display: block;
  width: 100%;
  cursor: pointer;
`;

// 모바일 브라우저는 input[type="date"]의 placeholder를 표시하지 않으므로
// 오버레이로 직접 그려준다 (값이 비어있을 때만 렌더)
// PC 브라우저는 빈 input에 "yyyy. mm. dd." 네이티브 표기가 보이므로
// background-color로 가려준다 (border 두께 1px 만큼 inset)
// pointer-events: none → 부모 <label>로 클릭 위임 (input.click 자동 발생)
const DatePlaceholder = styled.span`
  position: absolute;
  inset: 1px;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
  color: #999;
  background-color: #fff;
  border-radius: 9px;
  font-size: 16px;
  padding: 0 14px;
  white-space: nowrap;
  user-select: none;
`;

const MonthInput = styled.input`
  display: block;
  width: 100%;
  min-width: 0;
  max-width: 100%;
  box-sizing: border-box;
  height: 52px;
  padding: 0 14px;
  border-radius: 10px;
  border: 1px solid ${(props) => (props.$hasError ? "#ff4c4c" : "#e1e5ee")};
  background-color: #fff;
  font-size: 16px;
  color: #333;
  font-family: inherit;
  outline: none;
  text-align: center;
  transition: border-color 0.2s ease;
  -webkit-appearance: none;
  appearance: none;

  &:focus {
    border-color: ${(props) => (props.$hasError ? "#ff4c4c" : "#E31C20")};
  }

  /* iOS Safari: 내부 날짜 표기 요소가 영역을 넘는 문제 방지 + 수직·수평 중앙 정렬 */
  &::-webkit-date-and-time-value {
    text-align: center;
    min-width: 0;
    width: 100%;
    height: 100%;
    line-height: 52px;
    margin: 0;
  }

  /* iOS·Chrome: 내부 datetime-edit 영역을 풀-필 + flex로 수직·수평 중앙 정렬 */
  &::-webkit-datetime-edit {
    padding: 0;
    margin: 0;
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    line-height: 1;
  }

  &::-webkit-datetime-edit-fields-wrapper {
    padding: 0;
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &::-webkit-datetime-edit-text,
  &::-webkit-datetime-edit-year-field,
  &::-webkit-datetime-edit-month-field,
  &::-webkit-datetime-edit-day-field {
    padding: 0 1px;
  }
`;

const ToggleGroup = styled.div`
  display: flex;
  gap: 10px;
  width: 100%;
`;

const ToggleButton = styled.button`
  flex: 1;
  height: 52px;
  border-radius: 10px;
  border: 1px solid ${(props) => (props.$active ? "#E31C20" : "#e1e5ee")};
  background-color: ${(props) => (props.$active ? "#FCE5E6" : "#fff")};
  color: ${(props) => (props.$active ? "#E31C20" : "#555")};
  font-size: 15px;
  font-weight: ${(props) => (props.$active ? 700 : 500)};
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    border-color: #e31c20;
  }
`;

const FeeInputContainer = styled.div`
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px dashed #e1e5ee;
`;

const FeeInputWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
`;

const FeeInput = styled.input`
  width: 100%;
  height: 52px;
  padding: 0 36px 0 14px;
  border-radius: 10px;
  border: 1px solid #e1e5ee;
  background-color: #fff;
  font-size: 16px;
  color: #333;
  font-family: inherit;
  outline: none;
  text-align: right;
  transition: border-color 0.2s ease;

  &:focus {
    border-color: #e31c20;
  }
`;

const FeeUnit = styled.span`
  position: absolute;
  right: 14px;
  font-size: 16px;
  color: #555;
  pointer-events: none;
`;

const ErrorAlert = styled.div`
  width: 100%;
  padding: 12px;
  margin-bottom: 20px;
  background-color: rgba(255, 76, 76, 0.1);
  color: #ff4c4c;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  text-align: center;
`;

const CompleteButton = styled(motion.button)`
  display: flex;
  min-height: 56px;
  height: 56px;
  padding: 0 20px;
  justify-content: center;
  align-items: center;
  align-self: stretch;
  border-radius: 20px;
  background: ${(props) => (props.disabled ? "#E0898A" : "#E31C20")};
  color: ${(props) => (props.disabled ? "#DDC2C2" : "white")};
  font-size: 16px;
  font-weight: 600;
  cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};
  border: none;
  margin-top: 40px;

  @media (min-width: 769px) {
    max-width: 470px;
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 999;
  touch-action: none;
`;

const ModalContainer = styled.div`
  width: 90%;
  max-width: 355px;
  background-color: white;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
`;

const ModalContent = styled.div`
  padding: 30px 20px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const ModalText = styled.p`
  font-size: 16px;
  color: #333;
  text-align: left;
  margin-bottom: 30px;
  line-height: 1.5;
  width: 100%;
`;

const ModalButton = styled.button`
  width: 100%;
  height: 50px;
  background-color: #e31c20;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  align-self: center;
`;

export default ScreenshotSubmission;
